import {
  codeSeries,
  mapEtapaToGradeName,
  mapEtapaToSegment,
} from "@/lib/utils";

interface OrderItem {
  id: number;
  codigo_produto: string;
  descricao_produto: string;
  preco: number;
  ano_letivo: number;
  marca: string;
  sku: string;
  status: string;
  utilizacao?: string | null;
  serie?: string | null;
  segmento?: string | null;
  quantidade_contratual: number;
  desconto: number;
  adjustment: number;
  quantidade_pedido: number;
  valor_liquido: number;
  valor_total: number;
  delivery_type_id: number;
  delivery_type_number: number;
  brand_id: number;
}

interface GroupedProduct {
  id: number;
  /** Código do produto: distingue produtos de mesmo nome (ex.: o mesmo material em séries diferentes). */
  codigo?: string;
  nome: string;
  marca: string;
  brand_id: number;
  precoAluno2025: number;
  valorUnitario: number;
  qtdReferencia: number;
  quantidade: number;
  valorTotal: number;
  utilizacao?: string;
}

interface SeriesGroup {
  nome: string;
  produtos: GroupedProduct[];
  total: number;
  somaPrecoAluno2025: number;
  somaPrecoAluno2026: number;
}

interface BrandGroup {
  nome: string;
  brandId: number;
  produtos: GroupedProduct[];
  total: number;
  somaPrecoAluno2025: number;
  somaPrecoAluno2026: number;
}

interface SegmentGroup {
  nome: string;
  brands?: BrandGroup[];
  series?: SeriesGroup[];
  total: number;
}

// Map marca (which contains ID as string) to brand name
const getBrandName = (marca: string): string => {
  const brandMap: Record<string, string> = {
    "1": "AZ",
    "2": "High Five",
    "3": "My Life",
    "4": "LEX",
    "5": "AI4SCHOOL",
  };
  return brandMap[marca] || marca || "Sem Marca";
};

export const groupItemsBySegmentAndSeries = (
  items: OrderItem[]
): SegmentGroup[] => {
  // Filter items with quantity > 0
  const validItems = items.filter((item) => item.quantidade_pedido > 0);

  // Group by segment first, then by brand
  const segmentGroups: Record<string, Record<string, GroupedProduct[]>> = {};

  // Calculate Preço 2025: Valor Original = Valor Final / (1 + Percentual de Aumento)
  const calculatePrice2025 = (valorLiquido: number, adjustment: number) => {
    return valorLiquido / (1 + adjustment / 100);
  };

  // Helper function to sort products
  const sortProducts = (produtos: GroupedProduct[]) => {
    return produtos.sort((a, b) => {
      // Special handling for Plataforma AZ products to be sequential
      const aPlatform = a.nome.includes("Plataforma AZ");
      const bPlatform = b.nome.includes("Plataforma AZ");

      if (aPlatform && bPlatform) {
        const aFirst = a.nome.includes("1º Semestre");
        const bFirst = b.nome.includes("1º Semestre");
        if (aFirst && !bFirst) return -1;
        if (!aFirst && bFirst) return 1;
      }

      // Sort by product name alphabetically
      return a.nome.localeCompare(b.nome);
    });
  };

  validItems.forEach((item) => {
    const segment = mapEtapaToSegment(item.serie);
    const brandName = getBrandName(item.marca);
    const brandKey = brandName;

    if (!segmentGroups[segment]) {
      segmentGroups[segment] = {};
    }

    if (!segmentGroups[segment][brandKey]) {
      segmentGroups[segment][brandKey] = [];
    }

    const precoAluno2025 = calculatePrice2025(
      item.valor_liquido,
      item.adjustment
    );

    // Check if a product with the same description already exists
    const existingProduct = segmentGroups[segment][brandKey].find(
      (p) => p.nome === item.descricao_produto
    );

    if (existingProduct) {
      // Aggregate quantities and totals
      existingProduct.quantidade += item.quantidade_pedido;
      existingProduct.valorTotal += item.valor_total;
      existingProduct.qtdReferencia += item.quantidade_contratual;
      existingProduct.precoAluno2025 += precoAluno2025;
    } else {
      // Add new product
      segmentGroups[segment][brandKey].push({
        id: item.id,
        nome: item.descricao_produto,
        marca: brandName,
        brand_id: item.brand_id,
        precoAluno2025,
        valorUnitario: item.valor_liquido, // This is Preço por Aluno 2026
        qtdReferencia: item.quantidade_contratual,
        quantidade: item.quantidade_pedido,
        valorTotal: item.valor_total,
      });
    }
  });

  // Convert to structured format
  const result: SegmentGroup[] = [];

  Object.entries(segmentGroups).forEach(([segmentName, brandData]) => {
    const brandGroups: BrandGroup[] = [];
    let segmentTotal = 0;

    Object.entries(brandData).forEach(([brandKey, produtos]) => {
      // Sort products within each brand
      const sortedProdutos = sortProducts(produtos);

      const brandTotal = sortedProdutos.reduce(
        (sum, produto) => sum + produto.valorTotal,
        0
      );
      const somaPrecoAluno2025 = sortedProdutos.reduce(
        (sum, produto) => sum + produto.precoAluno2025,
        0
      );
      const somaPrecoAluno2026 = sortedProdutos.reduce(
        (sum, produto) => sum + produto.valorUnitario,
        0
      );

      segmentTotal += brandTotal;

      // Brand name is the key itself now
      const brandName = brandKey;
      const brandId = produtos[0]?.brand_id || 0;

      brandGroups.push({
        nome: brandName,
        brandId: brandId,
        produtos: sortedProdutos,
        total: brandTotal,
        somaPrecoAluno2025,
        somaPrecoAluno2026,
      });
    });

    // Sort brands by brand_id (ascending)
    brandGroups.sort((a, b) => a.brandId - b.brandId);

    result.push({
      nome: segmentName,
      brands: brandGroups,
      total: segmentTotal,
    });
  });

  // Sort segments by predefined order
  const segmentOrder: Record<string, number> = {
    "Ed. Infantil": 1,
    "Fund. Anos Iniciais": 2,
    "Fund. Anos Finais": 3,
    "Ensino Médio": 4,
    "PV": 5,
    "Outros": 999,
  };

  result.sort((a, b) => {
    const orderA = segmentOrder[a.nome] || 999;
    const orderB = segmentOrder[b.nome] || 999;
    return orderA - orderB;
  });

  return result;
};

export const groupItemsBySegmentAndSeriesForPDF = (
  items: OrderItem[]
): SegmentGroup[] => {
  // Filter items with quantity > 0
  const validItems = items.filter((item) => item.quantidade_pedido > 0);

  // Group by segment first
  const segmentGroups: Record<string, Record<string, GroupedProduct[]>> = {};

  // Calculate Preço 2025: Valor Original = Valor Final / (1 + Percentual de Aumento)
  const calculatePrice2025 = (valorLiquido: number, adjustment: number) => {
    return valorLiquido / (1 + adjustment / 100);
  };

  // Helper function to sort products
  const sortProducts = (produtos: GroupedProduct[]) => {
    return produtos.sort((a, b) => {
      // First sort by brand_id (priority: 1 = first, 2 = second, etc.)
      const brandPriorityComparison = a.brand_id - b.brand_id;
      if (brandPriorityComparison !== 0) return brandPriorityComparison;

      // Special handling for Plataforma AZ products to be sequential
      const aPlatform = a.nome.includes("Plataforma AZ");
      const bPlatform = b.nome.includes("Plataforma AZ");

      if (aPlatform && bPlatform) {
        const aFirst = a.nome.includes("1º Semestre");
        const bFirst = b.nome.includes("1º Semestre");
        if (aFirst && !bFirst) return -1;
        if (!aFirst && bFirst) return 1;
      }

      // Then sort by product name alphabetically
      return a.nome.localeCompare(b.nome);
    });
  };

  validItems.forEach((item) => {
    const segment = mapEtapaToSegment(item.serie);
    const series = mapEtapaToGradeName(item.serie);

    if (!segmentGroups[segment]) {
      segmentGroups[segment] = {};
    }

    if (!segmentGroups[segment][series]) {
      segmentGroups[segment][series] = [];
    }

    const precoAluno2025 = calculatePrice2025(
      item.valor_liquido,
      item.adjustment
    );

    segmentGroups[segment][series].push({
      id: item.id,
      codigo: item.codigo_produto,
      nome: item.descricao_produto,
      marca: item.marca,
      brand_id: item.brand_id,
      precoAluno2025,
      valorUnitario: item.valor_liquido, // This is Preço por Aluno 2026
      qtdReferencia: item.quantidade_contratual,
      quantidade: item.quantidade_pedido,
      valorTotal: item.valor_total,
      utilizacao: item.utilizacao,
    });
  });

  // Convert to structured format
  const result: SegmentGroup[] = [];

  Object.entries(segmentGroups).forEach(([segmentName, seriesData]) => {
    const seriesGroups: SeriesGroup[] = [];
    let segmentTotal = 0;

    Object.entries(seriesData).forEach(([seriesName, produtos]) => {
      // Sort products within each series
      const sortedProdutos = sortProducts(produtos);

      const seriesTotal = sortedProdutos.reduce(
        (sum, produto) => sum + produto.valorTotal,
        0
      );
      const somaPrecoAluno2025 = sortedProdutos.reduce(
        (sum, produto) => sum + produto.precoAluno2025,
        0
      );
      const somaPrecoAluno2026 = sortedProdutos.reduce(
        (sum, produto) => sum + produto.valorUnitario,
        0
      );

      segmentTotal += seriesTotal;

      seriesGroups.push({
        nome: seriesName,
        produtos: sortedProdutos,
        total: seriesTotal,
        somaPrecoAluno2025,
        somaPrecoAluno2026,
      });
    });

    // Sort series by grade level order (not alphabetically)
    seriesGroups.sort((a, b) => {
      return codeSeries(a.nome) - codeSeries(b.nome);
    });

    result.push({
      nome: segmentName,
      series: seriesGroups,
      total: segmentTotal,
    });
  });

  // Sort segments by predefined order
  const segmentOrder: Record<string, number> = {
    "Ed. Infantil": 1,
    "Fund. Anos Iniciais": 2,
    "Fund. Anos Finais": 3,
    "Ensino Médio": 4,
    "PV": 5,
    "Outros": 999,
  };

  result.sort((a, b) => {
    const orderA = segmentOrder[a.nome] || 999;
    const orderB = segmentOrder[b.nome] || 999;
    return orderA - orderB;
  });

  return result;
};

export const getSegmentIcon = (segmentName: string): string => {
  const iconMap: Record<string, string> = {
    "Educação Infantil": "EI",
    "Ed. Infantil": "EI",
    "Ensino Fundamental - Anos Iniciais": "AI",
    "Fund. Anos Iniciais": "AI",
    "Fundamental Anos Iniciais": "AI",
    "Ensino Fundamental - Anos Finais": "AF",
    "Fund. Anos Finais": "AF",
    "Fundamental Anos Finais": "AF",
    "Ensino Médio": "EM",
    "Pré Vestibular": "PV",
    PV: "PV",
    Literatura: "LIT",
    Paradidáticos: "PD",
    Complementares: "COMP",
    Dicionários: "DIC",
    Inglês: "ING",
    Outros: "SEG",
  };

  return iconMap[segmentName] || "SEG";
};
